import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useTranslation } from 'react-i18next';
import { usePlatformAccess } from '@/hooks/usePlatformAccess';
import { useGatingStore } from '@/stores/gating.store';
import {
  faLinkedin,
  faFacebook,
  faInstagram,
  faYoutube,
  faTiktok,
  faPinterest,
  faThreads,
  faGoogle,
  faBluesky,
} from '@fortawesome/free-brands-svg-icons';

/**
 * ConnectChannelStep Component
 *
 * Second step of onboarding wizard - connect first social media channel
 */

interface Platform {
  id: string;
  name: string;
  faIcon?: IconDefinition;
  customIcon?: string;
  color: string;
}

// Platform config - IDs match database enum values (uppercase)
// Matches ChannelSettings.tsx PLATFORM_CONFIG
const PLATFORM_CONFIG: Record<string, Platform> = {
  LINKEDIN: { id: 'LINKEDIN', name: 'LinkedIn', faIcon: faLinkedin, color: 'bg-[#0A66C2]' },
  FACEBOOK: { id: 'FACEBOOK', name: 'Facebook', faIcon: faFacebook, color: 'bg-[#1877F2]' },
  INSTAGRAM: { id: 'INSTAGRAM', name: 'Instagram', faIcon: faInstagram, color: 'bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#FCAF45]' },
  YOUTUBE: { id: 'YOUTUBE', name: 'YouTube', faIcon: faYoutube, color: 'bg-[#FF0000]' },
  TIKTOK: { id: 'TIKTOK', name: 'TikTok', faIcon: faTiktok, color: 'bg-black' },
  PINTEREST: { id: 'PINTEREST', name: 'Pinterest', faIcon: faPinterest, color: 'bg-[#E60023]' },
  THREADS: { id: 'THREADS', name: 'Threads', faIcon: faThreads, color: 'bg-black' },
  GOOGLE_MY_BUSINESS: { id: 'GOOGLE_MY_BUSINESS', name: 'Google Business', faIcon: faGoogle, color: 'bg-[#4285F4]' },
  BLUESKY: { id: 'BLUESKY', name: 'Bluesky', faIcon: faBluesky, color: 'bg-[#0085FF]' },
  CANVA: { id: 'CANVA', name: 'Canva', customIcon: '/assets/img/canva-circle.png', color: 'bg-[#00C4CC]' },
};

// Platforms shown in the connect grid (same order as ChannelSettings, excludes Canva)
const CONNECT_PLATFORMS = [
  'LINKEDIN', 'FACEBOOK', 'INSTAGRAM', 'YOUTUBE',
  'TIKTOK', 'PINTEREST', 'THREADS', 'GOOGLE_MY_BUSINESS', 'BLUESKY'
].map(id => PLATFORM_CONFIG[id]);

interface ConnectChannelStepProps {
  connectedChannels: string[];
  existingChannels: Array<{
    id: number;
    platform: string;
    platformUsername: string;
    metadata?: {
      avatar?: string;
      displayName?: string;
    };
  }>;
  onConnectChannel: (platformId: string) => void | Promise<void>;
}

export function ConnectChannelStep({ connectedChannels, existingChannels, onConnectChannel }: ConnectChannelStepProps) {
  const { t } = useTranslation('common');
  const { isPlatformLocked, upsellPlanSlugFor } = usePlatformAccess();
  const openUpsell = useGatingStore((state) => state.openUpsell);

  return (
    <div>
      {existingChannels.length > 0 && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Icon name={ICONS.CHECK_CIRCLE} size="md" color="text-green-600" ariaHidden />
            <p className="text-sm font-medium text-green-900">
              {t('onboarding_channel_already_connected', { count: existingChannels.length })}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {existingChannels.map((channel) => {
              const platform = PLATFORM_CONFIG[channel.platform];
              return (
                <div key={channel.id} className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full border border-green-200">
                  {platform && (
                    <div className={cn('w-6 h-6 rounded flex items-center justify-center text-white overflow-hidden', platform.color)}>
                      {platform.faIcon ? (
                        <FontAwesomeIcon icon={platform.faIcon} className="text-xs" aria-hidden="true" />
                      ) : platform.customIcon ? (
                        <img src={platform.customIcon} alt="" className="w-full h-full object-cover" />
                      ) : null}
                    </div>
                  )}
                  <span className="text-sm text-gray-700">{channel.metadata?.displayName || channel.platformUsername}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <h2 className="text-xl font-semibold text-gray-900 mb-2 text-center">
        {t('onboarding_channel_title')}
      </h2>
      <p className="text-gray-600 mb-6 text-center">
        {t('onboarding_channel_subtitle')}
      </p>

      {/* Platform Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {CONNECT_PLATFORMS.map((platform) => {
          const isConnected = connectedChannels.includes(platform.id);
          // Package does not include this platform: show it locked, the click explains instead of connecting
          const isLocked = !isConnected && isPlatformLocked(platform.id);

          return (
            <button
              key={platform.id}
              type="button"
              onClick={() =>
                isLocked
                  ? openUpsell(
                      { feature: `platform:${platform.id}`, variant: 'platform', platform: platform.id, upsellPlanSlug: upsellPlanSlugFor(platform.id) },
                      true
                    )
                  : void onConnectChannel(platform.id)
              }
              disabled={isConnected}
              data-testid={`onboarding-platform-${platform.id}`}
              data-locked={isLocked ? 'true' : undefined}
              className={cn(
                'relative aspect-square rounded-xl border-2 transition-all duration-200',
                'flex flex-col items-center justify-center gap-2',
                'hover:border-primary-400 hover:shadow-md',
                'focus:outline-none focus-visible:border-primary-500',
                'disabled:opacity-75 disabled:cursor-default',
                isConnected ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white',
                isLocked && 'bg-gray-50'
              )}
            >
              {/* Platform Icon */}
              <div
                className={cn(
                  'w-12 h-12 rounded-lg flex items-center justify-center text-white transition-transform hover:scale-105 overflow-hidden',
                  platform.color,
                  isLocked && 'opacity-50'
                )}
              >
                {platform.faIcon ? (
                  <FontAwesomeIcon icon={platform.faIcon} className="text-xl" aria-hidden="true" />
                ) : platform.customIcon ? (
                  <img src={platform.customIcon} alt="" className="w-full h-full object-cover" />
                ) : null}
              </div>

              {/* Platform Name */}
              <span className="text-sm font-medium text-gray-900 text-center">
                {platform.name}
              </span>

              {/* Locked: platform not in the package */}
              {isLocked && (
                <span className="flex items-center gap-1 text-[11px] font-medium leading-tight text-primary-700">
                  <Icon name={ICONS.LOCK} size="xs" ariaHidden />
                  {t('onboarding_channel_platform_locked')}
                </span>
              )}

              {/* Connected Badge */}
              {isConnected && (
                <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center shadow-md animate-fade-in">
                  <Icon name={ICONS.CHECK} size="sm" color="text-white" ariaHidden />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Connected Channels List */}
      {connectedChannels.length > 0 && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <Icon name={ICONS.CHECK_CIRCLE} size="md" color="text-primary-600" ariaHidden />
            <div className="flex-1">
              <p className="text-sm font-medium text-primary-900">
                {t('onboarding_channel_connected_count', { defaultValue: '{{count}} channel connected', count: connectedChannels.length })}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {connectedChannels.map((channelId) => {
                  const platform = PLATFORM_CONFIG[channelId];
                  return (
                    <div key={channelId} className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full border border-primary-200">
                      {platform && (
                        <div className={cn('w-5 h-5 rounded flex items-center justify-center text-white overflow-hidden', platform.color)}>
                          {platform.faIcon ? (
                            <FontAwesomeIcon icon={platform.faIcon} className="text-xs" aria-hidden="true" />
                          ) : platform.customIcon ? (
                            <img src={platform.customIcon} alt="" className="w-full h-full object-cover" />
                          ) : null}
                        </div>
                      )}
                      <span className="text-sm text-primary-700">{platform?.name || channelId}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Helper Text */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name={ICONS.BULB} size="md" color="text-gray-600" ariaHidden />
          <p className="text-sm text-gray-700">
            {existingChannels.length > 0
              ? t('onboarding_channel_tip_more')
              : t('onboarding_channel_tip_settings')}
          </p>
        </div>
      </div>
    </div>
  );
}
